/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import axios from "axios";
import { toast } from "sonner";
import {
  Image as ImageIcon, FloppyDisk, Plus, Trash as TrashIcon,
  ArrowSquareOut, Buildings, MapPin,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};

const emptyRow = { name: "", logo_url: "", alt: "" };
const emptyCity = { name: "", slug: "" };

export default function ServicesPageCms() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/services-page");
      setCfg(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero_image_url:  cfg.hero_image_url,
        hero_image_alt:  cfg.hero_image_alt,
        pillar_health_image_url:  cfg.pillar_health_image_url,
        pillar_hygiene_image_url: cfg.pillar_hygiene_image_url,
        pillar_bmw_image_url:     cfg.pillar_bmw_image_url,
        signup_url: cfg.signup_url,
        login_url:  cfg.login_url,
        partner_logos: (cfg.partner_logos || []).filter(p => p.name && p.logo_url),
        cities:        (cfg.cities || []).filter(c => c.name && c.slug),
      };
      const r = await api.put("/admin/services-page", payload);
      setCfg(r.data);
      toast.success("Services page saved");
    } catch (e) { toast.error(fmtErr(e)); }
    setSaving(false);
  };

  if (!cfg) {
    return (
      <AppShell>
        <div className="p-8 text-sm text-slate-500">Loading…</div>
      </AppShell>
    );
  }

  const ImgField = ({ label, k, hint }) => (
    <div data-testid={`cms-field-${k}`}>
      <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
        {label}
      </label>
      <div className="flex gap-3">
        <div className="w-20 h-20 shrink-0 rounded-xl bg-[#F7F6F2] border border-[#F0EEEA] overflow-hidden">
          {cfg[k] && (
            <img src={cfg[k]} alt="" className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.opacity = 0.3; }}/>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            value={cfg[k] || ""}
            onChange={(e) => set(k, e.target.value)}
            placeholder="https://…"
            data-testid={`cms-input-${k}`}
            className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"
          />
          {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
          {cfg[k] && (
            <a href={cfg[k]} target="_blank" rel="noreferrer"
               className="text-[11px] text-[#D23247] mt-1 inline-flex items-center gap-1">
              Open <ArrowSquareOut size={10} weight="bold"/>
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="services-page-cms">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">
              Content Management
            </div>
            <h1 className="font-heading text-3xl mt-1 text-slate-900">
              /services page settings
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Paste image URLs, partner logos and city chips shown on the
              public <code className="text-[#D23247]">/services</code> page.
            </p>
          </div>
          <div className="flex gap-2">
            <a href="/services" target="_blank" rel="noreferrer"
               data-testid="cms-preview"
               className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-sm">
              Preview <ArrowSquareOut size={12} weight="bold"/>
            </a>
            <button onClick={save} disabled={saving} data-testid="cms-save"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-white text-sm font-medium bg-[#D23247] hover:bg-[#B52236] disabled:opacity-60">
              <FloppyDisk size={14}/> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </header>

        {/* ---- Images ---- */}
        <section className="bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 space-y-5">
          <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
            <ImageIcon size={18} weight="duotone" className="text-[#D23247]"/>
            Images
          </h2>
          <ImgField label="Hero image (family photo)"      k="hero_image_url"
                     hint="Shown on the right side of the hero. 4:5 or 6:7 looks best."/>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
              Hero image alt text
            </label>
            <input value={cfg.hero_image_alt || ""} onChange={(e) => set("hero_image_alt", e.target.value)}
                   data-testid="cms-input-hero_image_alt"
                   className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm"/>
          </div>
          <ImgField label="Health Services pillar image"   k="pillar_health_image_url"/>
          <ImgField label="Hygiene Services pillar image"  k="pillar_hygiene_image_url"/>
          <ImgField label="BMW pillar image"               k="pillar_bmw_image_url"/>
        </section>

        {/* ---- Auth URLs ---- */}
        <section className="bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 space-y-4">
          <h2 className="font-heading text-xl text-slate-900">Sign-up / Login buttons</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                Sign up URL
              </label>
              <input value={cfg.signup_url || ""} onChange={(e) => set("signup_url", e.target.value)}
                     data-testid="cms-input-signup_url"
                     className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"/>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
                Login URL
              </label>
              <input value={cfg.login_url || ""} onChange={(e) => set("login_url", e.target.value)}
                     data-testid="cms-input-login_url"
                     className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"/>
            </div>
          </div>
        </section>

        {/* ---- Partner logos ---- */}
        <section className="bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
              <Buildings size={18} weight="duotone" className="text-[#D23247]"/>
              Partner logos (trust strip)
            </h2>
            <button
              onClick={() => set("partner_logos", [...(cfg.partner_logos || []), { ...emptyRow }])}
              data-testid="cms-partner-add"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-xs">
              <Plus size={12}/> Add logo
            </button>
          </div>
          {(cfg.partner_logos || []).length === 0 && (
            <div className="text-xs text-slate-500">
              Add one or more hospital / pharmacy logos — they appear in a grayscale strip under the hero.
            </div>
          )}
          <div className="space-y-3">
            {(cfg.partner_logos || []).map((p, i) => (
              <div key={i} className="grid sm:grid-cols-[80px_1fr_1fr_1fr_40px] gap-3 items-start"
                   data-testid={`cms-partner-row-${i}`}>
                <div className="w-20 h-14 rounded-md bg-[#F7F6F2] border border-[#F0EEEA] overflow-hidden">
                  {p.logo_url && <img src={p.logo_url} alt="" className="w-full h-full object-contain"/>}
                </div>
                <input value={p.name} onChange={(e) => {
                         const arr = [...cfg.partner_logos]; arr[i] = { ...arr[i], name: e.target.value };
                         set("partner_logos", arr);
                       }} placeholder="Name"
                       data-testid={`cms-partner-${i}-name`}
                       className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm"/>
                <input value={p.logo_url} onChange={(e) => {
                         const arr = [...cfg.partner_logos]; arr[i] = { ...arr[i], logo_url: e.target.value };
                         set("partner_logos", arr);
                       }} placeholder="Logo URL (https://…)"
                       data-testid={`cms-partner-${i}-url`}
                       className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"/>
                <input value={p.alt || ""} onChange={(e) => {
                         const arr = [...cfg.partner_logos]; arr[i] = { ...arr[i], alt: e.target.value };
                         set("partner_logos", arr);
                       }} placeholder="Alt text"
                       data-testid={`cms-partner-${i}-alt`}
                       className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm"/>
                <button onClick={() => {
                          const arr = [...cfg.partner_logos]; arr.splice(i, 1); set("partner_logos", arr);
                        }} data-testid={`cms-partner-${i}-del`}
                        className="h-10 w-10 rounded-md border border-slate-200 text-slate-500 hover:text-[#B12A1E] hover:border-[#B12A1E] flex items-center justify-center">
                  <TrashIcon size={14}/>
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ---- City chips ---- */}
        <section className="bg-white rounded-2xl border border-[#F0EEEA] p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
              <MapPin size={18} weight="duotone" className="text-[#D23247]"/>
              City chips (localised links)
            </h2>
            <button
              onClick={() => set("cities", [...(cfg.cities || []), { ...emptyCity }])}
              data-testid="cms-city-add"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-xs">
              <Plus size={12}/> Add city
            </button>
          </div>
          <p className="text-[12px] text-slate-500">
            Each chip deep-links to <code>/services/biomedical-waste-collection/&lt;slug&gt;</code>.
            Slugs: <code>trivandrum</code>, <code>kochi</code>, <code>kottayam</code>, <code>kerala</code>, <code>india</code>.
          </p>
          <div className="space-y-3">
            {(cfg.cities || []).map((c, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_40px] gap-3"
                   data-testid={`cms-city-row-${i}`}>
                <input value={c.name} onChange={(e) => {
                         const arr = [...cfg.cities]; arr[i] = { ...arr[i], name: e.target.value };
                         set("cities", arr);
                       }} placeholder="Thiruvananthapuram"
                       data-testid={`cms-city-${i}-name`}
                       className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm"/>
                <input value={c.slug} onChange={(e) => {
                         const arr = [...cfg.cities]; arr[i] = { ...arr[i], slug: e.target.value };
                         set("cities", arr);
                       }} placeholder="trivandrum"
                       data-testid={`cms-city-${i}-slug`}
                       className="h-10 w-full px-3 border border-[#D9D5C6] rounded-md text-sm font-mono"/>
                <button onClick={() => {
                          const arr = [...cfg.cities]; arr.splice(i, 1); set("cities", arr);
                        }} data-testid={`cms-city-${i}-del`}
                        className="h-10 w-10 rounded-md border border-slate-200 text-slate-500 hover:text-[#B12A1E] hover:border-[#B12A1E] flex items-center justify-center">
                  <TrashIcon size={14}/>
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
